import React, { useEffect, useMemo, useState } from "react";

import GraphPanel from "./components/GraphPanel";
import MetricChart from "./components/MetricChart";
import { telemetryData } from "./data/telemetry";
import { fetchEntities, fetchSeries, getApiInfo } from "./services/analyticsApi";

const NAV_ITEMS = [
  { key: "overview", label: "Overview", hash: "#/overview" },
  { key: "markets", label: "Markets", hash: "#/markets" },
  { key: "protocols", label: "Protocols", hash: "#/protocols" },
  { key: "graphs", label: "Graphs", hash: "#/graphs" },
  { key: "insights", label: "Insights", hash: "#/insights" },
  { key: "about", label: "About", hash: "#/about" }
];

const METRIC_LABELS = {
  aave_supply_raw: "Supply (raw)",
  uni_price: "Price",
  uni_liquidity: "Liquidity",
  camelot_depth: "Depth",
  camelot_reserve0: "Reserve0",
  camelot_reserve1: "Reserve1",
  totalSupply: "Total Supply",
  "slot0.sqrtPriceX96": "slot0 sqrtPriceX96",
  "slot0.tick": "slot0 tick",
  liquidity: "Liquidity",
  reserve0: "Reserve0",
  reserve1: "Reserve1",
  reserveBlockTimestamp: "Reserve Block Timestamp"
};

const METRIC_COLORS = {
  aave_supply_raw: "#35f2b1",
  uni_price: "#3aa6ff",
  uni_liquidity: "#93c5ff",
  camelot_depth: "#f3a340",
  camelot_reserve0: "#f5c26b",
  camelot_reserve1: "#f1b15a"
};

const DEFAULT_ROUTE = { page: "overview" };

const parseRoute = () => {
  const raw = window.location.hash.replace("#", "");
  if (!raw) return DEFAULT_ROUTE;
  const parts = raw.split("/").filter(Boolean);
  const page = parts[0];
  if (page === "market" && parts[1]) {
    return { page: "market", slug: parts[1] };
  }
  if (["overview", "markets", "protocols", "graphs", "insights", "about"].includes(page)) {
    return { page };
  }
  return DEFAULT_ROUTE;
};

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  const date = new Date(ts * 1000);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatNumberString = (value) => {
  if (value === null || value === undefined) return "-";
  const str = String(value);
  if (/^-?\d+$/.test(str)) {
    const negative = str.startsWith("-");
    const digits = negative ? str.slice(1) : str;
    const withCommas = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return negative ? `-${withCommas}` : withCommas;
  }
  if (/^-?\d+\.\d+$/.test(str)) {
    const negative = str.startsWith("-");
    const [whole, fraction] = (negative ? str.slice(1) : str).split(".");
    const trimmed = fraction.replace(/0+$/, "");
    const sliced = trimmed.slice(0, 6);
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const combined = sliced ? `${withCommas}.${sliced}` : withCommas;
    return negative ? `-${combined}` : combined;
  }
  return str;
};

const formatMetricValue = (metric, value, compact = false) => {
  if (value === null || value === undefined) return "-";
  if (compact && typeof value === "number") {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 3
    }).format(value);
  }
  return formatNumberString(value);
};

const shortenAddress = (address) => {
  if (!address) return "-";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const metricLabel = (metric) => METRIC_LABELS[metric] || metric;

const slugify = (value, fallback) => {
  if (!value) return fallback;
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return slug || fallback;
};

const protocolName = (protocol) => {
  if (!protocol) return "Unknown";
  if (protocol.includes("aave")) return "Aave";
  if (protocol.includes("uniswap")) return "Uniswap";
  if (protocol.includes("camelot")) return "Camelot";
  return protocol.replace(/_/g, " ");
};

const protocolKey = (protocol) => {
  if (!protocol) return "other";
  if (protocol.includes("aave")) return "aave";
  if (protocol.includes("uniswap")) return "uniswap";
  if (protocol.includes("camelot")) return "camelot";
  return "other";
};

const CHAIN_LABELS = {
  1: "Ethereum",
  42161: "Arbitrum"
};

const chainName = (chainId) => CHAIN_LABELS[chainId] || `Chain ${chainId ?? "?"}`;

const metricsByProtocol = (key) => {
  if (key === "aave") return { primary: "aave_supply_raw", secondary: [] };
  if (key === "uniswap") return { primary: "uni_price", secondary: ["uni_liquidity"] };
  if (key === "camelot") return { primary: "camelot_depth", secondary: ["camelot_reserve0", "camelot_reserve1"] };
  return { primary: "totalSupply", secondary: [] };
};

const formatUtc = (ts) =>
  new Date(ts * 1000).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short"
  });

const computeInsightForSeries = (rows, metric) => {
  if (!rows?.length) return null;
  const points = rows
    .map((row) => ({ ts: row.ts, value: Number(row.value) }))
    .filter((row) => Number.isFinite(row.value))
    .sort((a, b) => a.ts - b.ts);

  if (!points.length) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const minPoint = points.reduce((acc, row) => (row.value < acc.value ? row : acc), points[0]);
  const maxPoint = points.reduce((acc, row) => (row.value > acc.value ? row : acc), points[0]);
  const pctDelta =
    first.value === 0 ? null : ((last.value - first.value) / Math.abs(first.value)) * 100;
  const pctText = pctDelta === null ? "n/a" : `${pctDelta >= 0 ? "+" : ""}${pctDelta.toFixed(2)}%`;

  const caption = `${points.length} pts since ${formatUtc(first.ts)}; ${formatMetricValue(
    metric,
    first.value
  )} → ${formatMetricValue(metric, last.value)} (${pctText}); range ${formatMetricValue(
    metric,
    minPoint.value
  )}–${formatMetricValue(metric, maxPoint.value)}; last ${formatUtc(last.ts)}`;

  return {
    caption
  };
};

export default function App() {
  const [route, setRoute] = useState(parseRoute());
  const activeNavKey = route.page === "market" ? "markets" : route.page;
  const [data, setData] = useState(() => ({
    entities: telemetryData.entities,
    analyticsSeries: telemetryData.analyticsSeries,
    snapshots: telemetryData.snapshots,
    runs: telemetryData.runs,
    loadedFromApi: false
  }));
  const [apiState, setApiState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setApiState({ status: "loading", message: "" });
      try {
        const [entities, series] = await Promise.all([
          fetchEntities(),
          fetchSeries({ period: "hour", limit: 2000, order: "asc" })
        ]);
        if (!active) return;

        const overrides = new Map(
          telemetryData.entities.map((entity) => [entity.address.toLowerCase(), entity])
        );

        const normalizedEntities = entities.map((row) => {
          const address = String(row.address || "").toLowerCase();
          const override = overrides.get(address);
          const key = protocolKey(String(row.protocol || ""));
          const metrics = metricsByProtocol(override?.protocolKey || key);
          const label = row.label || override?.label || address;
          return {
            id: row.id,
            slug: override?.slug || slugify(label, `entity-${row.id}`),
            protocol: override?.protocol || protocolName(row.protocol || ""),
            protocolKey: override?.protocolKey || key,
            chainId: row.chain_id ?? override?.chainId,
            chain: chainName(row.chain_id),
            label,
            address,
            primaryMetric: override?.primaryMetric || metrics.primary,
            secondaryMetrics: override?.secondaryMetrics || metrics.secondary
          };
        });

        const normalizedSeries = series.map((row) => ({
          entityId: row.entity_id ?? row.entityId,
          period: row.period,
          ts: row.ts,
          metric: row.metric,
          value: row.value,
          sourceRunId: row.source_run_id ?? row.sourceRunId
        }));

        setData((prev) => ({
          ...prev,
          entities: normalizedEntities,
          analyticsSeries: normalizedSeries,
          loadedFromApi: true
        }));
        setApiState({ status: "ok", message: "" });
      } catch (err) {
        if (!active) return;
        setApiState({ status: "error", message: err?.message || String(err) });
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = "#/overview";
    }
    const onHashChange = () => setRoute(parseRoute());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const derived = useMemo(() => {
    const entityMap = new Map(data.entities.map((entity) => [entity.id, entity]));
    const latestByKey = new Map();
    const latestByEntity = new Map();

    data.analyticsSeries.forEach((row) => {
      const key = `${row.entityId}:${row.metric}`;
      const current = latestByKey.get(key);
      if (!current || row.ts > current.ts) {
        latestByKey.set(key, row);
      }
    });

    latestByKey.forEach((row) => {
      if (!latestByEntity.has(row.entityId)) {
        latestByEntity.set(row.entityId, new Map());
      }
      latestByEntity.get(row.entityId).set(row.metric, row);
    });

    const latestMetrics = Array.from(latestByKey.values())
      .map((row) => ({ ...row, entity: entityMap.get(row.entityId) }))
      .sort((a, b) => b.ts - a.ts);

    const lastSeriesTs = data.analyticsSeries.reduce((max, row) => Math.max(max, row.ts), 0);
    const lastSnapshotTs = data.loadedFromApi
      ? lastSeriesTs
      : data.snapshots.reduce((max, row) => Math.max(max, row.timestamp), 0);
    const lastRunTs = data.loadedFromApi
      ? lastSeriesTs
      : data.runs.reduce((max, row) => Math.max(max, row.endTs || 0), 0);

    const seriesByKey = new Map();
    data.analyticsSeries.forEach((row) => {
      const key = `${row.entityId}:${row.metric}`;
      if (!seriesByKey.has(key)) {
        seriesByKey.set(key, []);
      }
      seriesByKey.get(key).push(row);
    });

    const insightsByKey = new Map();
    seriesByKey.forEach((rows, key) => {
      const [, metric] = key.split(":");
      const insight = computeInsightForSeries(rows, metric);
      if (insight) {
        insightsByKey.set(key, insight);
      }
    });

    return {
      entityMap,
      latestByEntity,
      latestMetrics,
      lastSnapshotTs,
      lastRunTs,
      seriesByKey,
      insightsByKey
    };
  }, [data]);

  const marketsRows = data.entities.map((entity) => {
    const metrics = derived.latestByEntity.get(entity.id) || new Map();
    const primary = metrics.get(entity.primaryMetric);
    const secondaryValues = entity.secondaryMetrics.map((metric) => ({
      metric,
      row: metrics.get(metric)
    }));

    const latestTs = Math.max(
      ...Array.from(metrics.values()).map((row) => row.ts || 0),
      0
    );

    return {
      entity,
      primary,
      secondaryValues,
      latestTs
    };
  });

  const selectedEntity =
    route.page === "market"
      ? data.entities.find((entity) => entity.slug === route.slug)
      : null;

  const selectedSeries = selectedEntity
    ? data.analyticsSeries
        .filter((row) => row.entityId === selectedEntity.id)
        .sort((a, b) => b.ts - a.ts)
        .slice(0, 5)
    : [];

  const selectedSnapshots = selectedEntity
    ? data.snapshots
        .filter((row) => row.entityId === selectedEntity.id)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 5)
    : [];

  const selectedLatestMetrics = selectedEntity
    ? Array.from(derived.latestByEntity.get(selectedEntity.id)?.values() || [])
        .sort((a, b) => metricLabel(a.metric).localeCompare(metricLabel(b.metric)))
    : [];

  const primarySeries = selectedEntity
    ? derived.seriesByKey.get(`${selectedEntity.id}:${selectedEntity.primaryMetric}`) || []
    : [];
  const secondarySeries = selectedEntity
    ? selectedEntity.secondaryMetrics.map((metric) => ({
        metric,
        rows: derived.seriesByKey.get(`${selectedEntity.id}:${metric}`) || []
      }))
    : [];

  const overviewCharts = data.entities.map((entity) => ({
    entity,
    rows: derived.seriesByKey.get(`${entity.id}:${entity.primaryMetric}`) || []
  }));
  const apiInfo = getApiInfo();
  const chainsCovered = Array.from(new Set(data.entities.map((entity) => entity.chain).filter(Boolean)));
  const chainsLabel = chainsCovered.length ? chainsCovered.join(", ") : "Unknown";
  const apiStatusText = data.loadedFromApi ? "Live API" : "Mock fallback";
  const apiStatusDetail =
    apiState.status === "loading"
      ? "Loading API..."
      : apiState.status === "error"
      ? `API error: ${apiState.message}`
      : apiStatusText;

  const graphSets = useMemo(() => {
    const makeId = (prefix, value) => `${prefix}-${slugify(String(value), prefix)}`;
    const entityNodes = data.entities.map((entity) => ({
      id: `entity-${entity.id}`,
      label: entity.label,
      group: "entity"
    }));
    const protocolNodes = Array.from(
      new Map(
        data.entities
          .filter((entity) => entity.protocol)
          .map((entity) => [
            entity.protocol,
            { id: makeId("protocol", entity.protocol), label: entity.protocol, group: "protocol" }
          ])
      ).values()
    );
    const chainNodes = Array.from(
      new Map(
        data.entities
          .filter((entity) => entity.chain)
          .map((entity) => [
            entity.chain,
            { id: makeId("chain", entity.chain), label: entity.chain, group: "chain" }
          ])
      ).values()
    );

    const protocolEntityLinks = data.entities.flatMap((entity) => {
      const protocolId = makeId("protocol", entity.protocol);
      const entityId = `entity-${entity.id}`;
      const chainId = makeId("chain", entity.chain);
      return [
        { from: protocolId, to: entityId },
        { from: entityId, to: chainId }
      ];
    });

    const protocolEntityChain = {
      key: "protocol-entity-chain",
      title: "Protocol -> Entity -> Chain",
      description:
        "Shows what we track and where it lives. This helps you see coverage gaps and avoid blind spots.",
      columns: ["protocol", "entity", "chain"],
      nodes: [...protocolNodes, ...entityNodes, ...chainNodes],
      links: protocolEntityLinks,
      legend: [
        { label: "Protocol", className: "protocol" },
        { label: "Entity", className: "entity" },
        { label: "Chain", className: "chain" }
      ]
    };

    const uniswapEntities = data.entities.filter((entity) => entity.protocolKey === "uniswap");
    const uniswapEntityNodes = uniswapEntities.map((entity) => ({
      id: `entity-${entity.id}`,
      label: entity.label,
      group: "entity"
    }));
    const uniswapProtocolNodes = Array.from(
      new Map(
        uniswapEntities.map((entity) => [
          entity.protocol,
          { id: makeId("protocol", entity.protocol), label: entity.protocol, group: "protocol" }
        ])
      ).values()
    );
    const uniswapChainNodes = Array.from(
      new Map(
        uniswapEntities.map((entity) => [
          entity.chain,
          { id: makeId("chain", entity.chain), label: entity.chain, group: "chain" }
        ])
      ).values()
    );
    const uniswapLinks = uniswapEntities.flatMap((entity) => {
      const protocolId = makeId("protocol", entity.protocol);
      const entityId = `entity-${entity.id}`;
      const chainId = makeId("chain", entity.chain);
      return [
        { from: protocolId, to: entityId },
        { from: entityId, to: chainId }
      ];
    });

    const uniswapOnly = {
      key: "uniswap-only",
      title: "Uniswap Only",
      description:
        "Focuses only on Uniswap markets so you can compare them side by side and notice outliers quickly.",
      columns: ["protocol", "entity", "chain"],
      nodes: [...uniswapProtocolNodes, ...uniswapEntityNodes, ...uniswapChainNodes],
      links: uniswapLinks,
      legend: [
        { label: "Protocol", className: "protocol" },
        { label: "Entity", className: "entity" },
        { label: "Chain", className: "chain" }
      ]
    };

    const latestEntities = data.entities.slice(0, 6);
    const latestEntityNodes = latestEntities.map((entity) => ({
      id: `entity-${entity.id}`,
      label: entity.label,
      group: "entity"
    }));
    const latestSeriesNodes = latestEntities
      .map((entity) => {
        const latest = derived.latestByEntity.get(entity.id)?.get(entity.primaryMetric);
        if (!latest) return null;
        return {
          id: `series-${entity.id}-${latest.metric}`,
          label: `${metricLabel(latest.metric)} ${formatMetricValue(latest.metric, latest.value, true)}`,
          group: "series"
        };
      })
      .filter(Boolean);
    const latestSeriesLinks = latestSeriesNodes.map((node) => {
      const entityId = node.id.split("-")[1];
      return { from: `entity-${entityId}`, to: node.id };
    });
    const entityLatestSeries = {
      key: "entity-latest-series",
      title: "Entities + Latest Series Point",
      description:
        "Shows the newest reading for each market. Big jumps here can signal sudden risk changes.",
      columns: ["entity", "series"],
      nodes: [...latestEntityNodes, ...latestSeriesNodes],
      links: latestSeriesLinks,
      legend: [
        { label: "Entity", className: "entity" },
        { label: "Series", className: "series" }
      ]
    };

    const sampleEntity = data.entities[0];
    const sampleSnapshots = sampleEntity
      ? data.snapshots
          .filter((row) => row.entityId === sampleEntity.id)
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 10)
      : [];
    const snapshotNodes = sampleSnapshots.map((row, index) => ({
      id: `snapshot-${row.entityId}-${index}`,
      label: `${metricLabel(row.metric)} ${formatTimestamp(row.timestamp)}`,
      group: "snapshot"
    }));
    const snapshotLinks = sampleEntity
      ? snapshotNodes.map((node) => ({ from: `entity-${sampleEntity.id}`, to: node.id }))
      : [];
    const entitySnapshots = {
      key: "entity-snapshots",
      title: "Entity + Last 10 Snapshots",
      description:
        "Shows recent raw readings for one market. Consistent values build confidence; sharp moves deserve attention.",
      columns: ["entity", "snapshot"],
      nodes: sampleEntity
        ? [{ id: `entity-${sampleEntity.id}`, label: sampleEntity.label, group: "entity" }, ...snapshotNodes]
        : [],
      links: snapshotLinks,
      legend: [
        { label: "Entity", className: "entity" },
        { label: "Snapshot", className: "snapshot" }
      ]
    };

    const seriesWithRun = data.analyticsSeries.filter((row) => row.sourceRunId);
    const runIds = new Set();
    data.runs.forEach((run) => runIds.add(run.id));
    seriesWithRun.forEach((row) => runIds.add(row.sourceRunId));
    const runNodes = Array.from(runIds).map((id) => ({
      id: `run-${id}`,
      label: `Run ${id}`,
      group: "run"
    }));
    const runSeriesNodes = seriesWithRun.slice(0, 12).map((row, index) => ({
      id: `series-${row.entityId}-${row.metric}-${index}`,
      label: `${metricLabel(row.metric)} ${formatMetricValue(row.metric, row.value, true)}`,
      group: "series",
      runId: row.sourceRunId
    }));
    const runSeriesLinks = runSeriesNodes
      .filter((row) => row.runId)
      .map((row) => ({ from: `run-${row.runId}`, to: row.id }));
    const runsToSeries = {
      key: "runs-series",
      title: "Runs -> Series Lineage",
      description:
        "Shows each hourly update and what it produced. This helps confirm data freshness and spot missed updates.",
      columns: ["run", "series"],
      nodes: [...runNodes, ...runSeriesNodes],
      links: runSeriesLinks,
      legend: [
        { label: "Run", className: "run" },
        { label: "Series", className: "series" }
      ]
    };

    const coverageEntities = data.entities.filter((entity) => {
      if (entity.chainId === 1 || entity.chainId === 42161) return true;
      return ["Ethereum", "Arbitrum"].includes(entity.chain);
    });
    const coverageChains = Array.from(
      new Map(
        coverageEntities.map((entity) => [
          entity.chain,
          { id: makeId("chain", entity.chain), label: entity.chain, group: "chain" }
        ])
      ).values()
    );
    const coverageEntityNodes = coverageEntities.map((entity) => ({
      id: `entity-${entity.id}`,
      label: entity.label,
      group: "entity"
    }));
    const coverageLinks = coverageEntities.map((entity) => ({
      from: makeId("chain", entity.chain),
      to: `entity-${entity.id}`
    }));
    const chainCoverage = {
      key: "chain-coverage",
      title: "Ethereum vs Arbitrum Entities",
      description:
        "Shows what is on Ethereum versus Arbitrum. Too much on one chain can increase concentration risk.",
      columns: ["chain", "entity"],
      nodes: [...coverageChains, ...coverageEntityNodes],
      links: coverageLinks,
      legend: [
        { label: "Chain", className: "chain" },
        { label: "Entity", className: "entity" }
      ]
    };

    const sampleEntities = data.entities.slice(0, 5);
    const sampleProtocolNodes = Array.from(
      new Map(
        sampleEntities.map((entity) => [
          entity.protocol,
          { id: makeId("protocol", entity.protocol), label: entity.protocol, group: "protocol" }
        ])
      ).values()
    );
    const sampleEntityNodes = sampleEntities.map((entity) => ({
      id: `entity-${entity.id}`,
      label: entity.label,
      group: "entity"
    }));
    const sampleSeriesNodes = sampleEntities
      .map((entity) => {
        const latest = derived.latestByEntity.get(entity.id)?.get(entity.primaryMetric);
        if (!latest) return null;
        return {
          id: `series-${entity.id}-${latest.metric}`,
          label: `${metricLabel(latest.metric)} ${formatMetricValue(latest.metric, latest.value, true)}`,
          group: "series"
        };
      })
      .filter(Boolean);
    const sampleLinks = [
      ...sampleEntities.map((entity) => ({
        from: makeId("protocol", entity.protocol),
        to: `entity-${entity.id}`
      })),
      ...sampleSeriesNodes.map((node) => {
        const entityId = node.id.split("-")[1];
        return { from: `entity-${entityId}`, to: node.id };
      })
    ];
    const fullSample = {
      key: "full-sample",
      title: "Full Graph Sample",
      description:
        "A small snapshot of the whole network. It helps you see how everything connects at a glance.",
      columns: ["protocol", "entity", "series"],
      nodes: [...sampleProtocolNodes, ...sampleEntityNodes, ...sampleSeriesNodes],
      links: sampleLinks,
      legend: [
        { label: "Protocol", className: "protocol" },
        { label: "Entity", className: "entity" },
        { label: "Series", className: "series" }
      ]
    };

    return [
      protocolEntityChain,
      uniswapOnly,
      entityLatestSeries,
      entitySnapshots,
      runsToSeries,
      chainCoverage,
      fullSample
    ];
  }, [data, derived]);

  return (
    <div className="app">
      <header className="top-nav">
        <div className="nav-inner">
          <div className="brand">
            <img src="/assets/tradersensei-icon.png" alt="TraderSensei" />
            <div>
              <div className="brand-title">TraderSensei DeFi Analytics</div>
            <div className="brand-sub">{chainsLabel} telemetry, hourly snapshots</div>
            </div>
          </div>
          <nav className="nav-links">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                className={`nav-link${activeNavKey === item.key ? " active" : ""}`}
                href={item.hash}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="main">
        {route.page === "overview" && (
          <section className="animate-in">
            <h1 className="page-title">Overview</h1>
            <p className="page-subtitle">
              Live read-only telemetry pulled from the defi-analytics worker. Data is
              refreshed hourly and surfaces the latest Aave, Uniswap, and Camelot
              market state on {chainsLabel}.
            </p>
            <div className="api-status mono">{apiStatusDetail}</div>

            <div className="section stats-grid animate-in delay-1">
              <div className="stat-card">
                <div className="stat-label">Tracked Markets</div>
                <div className="stat-value">{data.entities.length}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Last Snapshot Time</div>
                <div className="stat-value">{formatTimestamp(derived.lastSnapshotTs)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Last Analytics Run</div>
                <div className="stat-value">{formatTimestamp(derived.lastRunTs)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Chains Covered</div>
                <div className="stat-value">{chainsLabel}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Data Status</div>
                <div className="stat-value">
                  <span className="status-chip">Live - Hourly</span>
                </div>
              </div>
            </div>

            <div className="section chart-grid animate-in delay-2">
              {overviewCharts.map((item) => (
                <MetricChart
                  key={item.entity.id}
                  title={item.entity.label}
                  subtitle={`${item.entity.protocol} · ${metricLabel(item.entity.primaryMetric)}`}
                  rows={item.rows}
                  color={METRIC_COLORS[item.entity.primaryMetric] || "#35f2b1"}
                  formatValue={(value, compact) => formatMetricValue(item.entity.primaryMetric, value, compact)}
                  formatTime={(value, compact) => (compact ? new Date(value * 1000).toLocaleDateString() : formatTimestamp(value))}
                  insight={derived.insightsByKey.get(`${item.entity.id}:${item.entity.primaryMetric}`)}
                />
              ))}
            </div>

            <div className="section table-shell animate-in delay-2">
              <div className="table-header">
                <div className="table-title">Latest Metrics</div>
                <div className="metric-pill">API: {apiInfo.baseUrl.replace(/^https?:\/\//, "")}</div>
              </div>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Protocol</th>
                      <th>Market / Pair</th>
                      <th>Metric</th>
                      <th>Latest Value</th>
                      <th>Period</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {derived.latestMetrics.map((row) => (
                      <tr key={`${row.entityId}-${row.metric}`}>
                        <td>{row.entity?.protocol}</td>
                        <td>{row.entity?.label}</td>
                        <td>{metricLabel(row.metric)}</td>
                        <td className="mono">{formatMetricValue(row.metric, row.value)}</td>
                        <td>{row.period}</td>
                        <td>{formatTimestamp(row.ts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {(route.page === "markets" || route.page === "protocols") && (
          <section className="animate-in">
            <h1 className="page-title">{route.page === "markets" ? "Markets" : "Protocols"}</h1>
            <p className="page-subtitle">
              One row per tracked entity. Primary metrics surface the most
              important telemetry signal for each protocol. Click a row to inspect
              raw snapshots.
            </p>

            <div className="section table-shell animate-in delay-1">
              <div className="table-header">
                <div className="table-title">Tracked Entities</div>
                <div className="status-chip">{data.entities.length} markets</div>
              </div>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Protocol</th>
                      <th>Market / Pair</th>
                      <th>Chain</th>
                      <th>Primary Metric</th>
                      <th>Secondary Metric</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marketsRows.map((row) => (
                      <tr key={row.entity.id}>
                        <td>{row.entity.protocol}</td>
                        <td className="link-cell">
                          <a href={`#/market/${row.entity.slug}`}>{row.entity.label}</a>
                        </td>
                        <td>{row.entity.chain}</td>
                        <td className="mono">
                          {metricLabel(row.entity.primaryMetric)}: {" "}
                          {row.primary ? formatMetricValue(row.entity.primaryMetric, row.primary.value) : "-"}
                        </td>
                        <td className="mono">
                          {row.secondaryValues.length === 0
                            ? "-"
                            : row.secondaryValues
                                .map((item) =>
                                  item.row
                                    ? `${metricLabel(item.metric)}: ${formatMetricValue(item.metric, item.row.value)}`
                                    : `${metricLabel(item.metric)}: -`
                                )
                                .join(" | ")}
                        </td>
                        <td>{row.latestTs ? formatTimestamp(row.latestTs) : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {route.page === "graphs" && (
          <section className="animate-in">
            <h1 className="page-title">Graphs</h1>
            <p className="page-subtitle">
              Powered by Neo4J Cypher graphing data to surface relationships that affect risk.
            </p>

            <div className="section graph-grid animate-in delay-1">
              {graphSets.map((graph) => (
                <GraphPanel
                  key={graph.key}
                  title={graph.title}
                  description={graph.description}
                  columns={graph.columns}
                  nodes={graph.nodes}
                  links={graph.links}
                  legend={graph.legend}
                />
              ))}
            </div>
          </section>
        )}

        {route.page === "market" && selectedEntity && (
          <section className="animate-in">
            <h1 className="page-title">Market Detail</h1>
            <p className="page-subtitle">
              Raw telemetry for {selectedEntity.label}. Use this view to validate
              the analytics series and compare with on-chain snapshots.
            </p>

            <div className="section detail-header animate-in delay-1">
              <div>
                <div className="metric-pill">{selectedEntity.protocol}</div>
                <h2 style={{ margin: "12px 0 8px" }}>{selectedEntity.label}</h2>
                <div className="detail-meta">
                  <span>Chain: {selectedEntity.chain}</span>
                  <span className="mono" title={selectedEntity.address}>
                    Address: {shortenAddress(selectedEntity.address)}
                  </span>
                </div>
              </div>
              <a className="button-link" href="#/markets">
                Back to Markets
              </a>
            </div>

            <div className="section chart-grid animate-in delay-1">
              <MetricChart
                title={metricLabel(selectedEntity.primaryMetric)}
                subtitle="Primary metric · hourly series"
                rows={primarySeries}
                color={METRIC_COLORS[selectedEntity.primaryMetric] || "#35f2b1"}
                formatValue={(value, compact) => formatMetricValue(selectedEntity.primaryMetric, value, compact)}
                formatTime={(value, compact) => (compact ? new Date(value * 1000).toLocaleDateString() : formatTimestamp(value))}
                insight={derived.insightsByKey.get(
                  `${selectedEntity.id}:${selectedEntity.primaryMetric}`
                )}
              />
              {secondarySeries.map((item) => (
                <MetricChart
                  key={item.metric}
                  title={metricLabel(item.metric)}
                  subtitle="Secondary metric · hourly series"
                  rows={item.rows}
                  color={METRIC_COLORS[item.metric] || "#3aa6ff"}
                  formatValue={(value, compact) => formatMetricValue(item.metric, value, compact)}
                  formatTime={(value, compact) => (compact ? new Date(value * 1000).toLocaleDateString() : formatTimestamp(value))}
                  insight={derived.insightsByKey.get(`${selectedEntity.id}:${item.metric}`)}
                />
              ))}
            </div>

            <div className="section metric-grid animate-in delay-1">
              {selectedLatestMetrics.map((row) => (
                <div key={row.metric} className="metric-card">
                  <div className="metric-label">{metricLabel(row.metric)}</div>
                  <div className="metric-value mono">{formatMetricValue(row.metric, row.value)}</div>
                  <div className="metric-label" style={{ marginTop: "6px" }}>
                    Updated {formatTimestamp(row.ts)}
                  </div>
                </div>
              ))}
            </div>

            <div className="section table-shell animate-in delay-2">
              <div className="table-header">
                <div className="table-title">Latest Analytics Series</div>
                <div className="metric-pill">Last 5 rows</div>
              </div>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Value</th>
                      <th>Period</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSeries.map((row, index) => (
                      <tr key={`${row.metric}-${row.ts}-${index}`}>
                        <td>{metricLabel(row.metric)}</td>
                        <td className="mono">{formatMetricValue(row.metric, row.value)}</td>
                        <td>{row.period}</td>
                        <td>{formatTimestamp(row.ts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="section table-shell animate-in delay-2">
              <div className="table-header">
                <div className="table-title">Latest Raw Snapshots</div>
                <div className="metric-pill">Last 5 rows</div>
              </div>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Value</th>
                      <th>Block</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSnapshots.map((row, index) => (
                      <tr key={`${row.metric}-${row.timestamp}-${index}`}>
                        <td>{metricLabel(row.metric)}</td>
                        <td className="mono">{formatMetricValue(row.metric, row.value)}</td>
                        <td>{row.blockNumber}</td>
                        <td>{formatTimestamp(row.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {route.page === "market" && !selectedEntity && (
          <section className="animate-in">
            <h1 className="page-title">Market not found</h1>
            <p className="page-subtitle">The requested market does not exist in the current telemetry set.</p>
          </section>
        )}

        {route.page === "insights" && (
          <section className="animate-in">
            <h1 className="page-title">Insights</h1>
            <p className="page-subtitle">
              Automated insights are generated after 24 hours of continuous data
              collection. This feed will populate once deltas are available.
            </p>
            <div className="section empty-state animate-in delay-1">
              No insights available yet. Insights appear after 24 hours of continuous data collection.
            </div>
          </section>
        )}

        {route.page === "about" && (
          <section className="animate-in">
            <h1 className="page-title">About / Methodology</h1>
            <p className="page-subtitle">
              TraderSensei DeFi Analytics surfaces read-only telemetry captured by
              the defi-analytics worker. The system snapshots protocol state on
              {chainsLabel}, materializes hourly series, and exposes the latest
              metrics for credibility and monitoring.
            </p>
            <div className="section table-shell animate-in delay-1">
              <div className="table-header">
                <div className="table-title">Data Pipeline</div>
                <div className="metric-pill">Read-only</div>
              </div>
              <div style={{ padding: "18px" }}>
                <p style={{ marginTop: 0 }}>
                  Snapshot jobs call on-chain contracts for Aave, Uniswap V3, and
                  Camelot, then write raw metrics into D1. Hourly materialization
                  compacts those snapshots into analytics series. This UI only
                  reads the latest series and raw samples.
                </p>
                <p style={{ marginBottom: 0, color: "var(--muted)" }}>
                  No user actions, trades, or wallet access are enabled in this MVP.
                  The focus is visibility and correctness before API expansion.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <div>TraderSensei DeFi Analytics - read-only telemetry MVP.</div>
          <div>Data status: Live - Hourly. Chains: {chainsLabel}.</div>
        </div>
      </footer>
    </div>
  );
}
